import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  MdFactCheck,
  MdCheckCircle,
  MdPending,
  MdWarningAmber,
  MdSearch,
  MdRefresh,
  MdFileDownload,
  MdCloudUpload,
  MdAdd,
  MdDeleteOutline,
  MdClose,
  MdInfoOutline,
  MdPerson,
  MdLogout,
  MdCheck,
  MdCancel,
  MdContentCopy,
  MdHourglassEmpty,
  MdPhotoCamera,
  MdZoomIn,
} from "react-icons/md";
import { apiFetch } from "../../services/api";
import { getCurrentUserRole, hasPermission, normalizeRole } from "../../utils/rbac";
import ShowSnacker from "./ShowSnacker";
import ErrorPopup from "./ErrorPopup";
import {
  useVisualReferenceImages,
  IqcCheckpointThumbnail,
  IqcReferenceLightboxModal,
} from "./IqcVisualReferenceModal";
import "../../styles/DashBoard_MasterMac.css";

// Helper to format any MAC ID with colons (XX:XX:XX:XX:XX:XX)
export const formatMacWithColon = (raw: any): string => {
  if (!raw) return "";
  const cleaned = String(raw).trim().toUpperCase().replace(/[^0-9A-F]/g, "");
  if (cleaned.length === 12) {
    return cleaned.match(/.{1,2}/g)?.join(":") || cleaned;
  }
  return String(raw).trim().toUpperCase();
};

// IQC Record Interface
export interface IqcRecord {
  id: number;
  macId: string;
  modelName?: string;
  visualQcStatus?: string;
  functionalQcStatus?: string;
  finalStatus?: string;
  overall_status?: string;
  inspector_name?: string;
  qcInspector?: string;
  notes?: string;
  visualQcName?: string;
  functionalQcName?: string;
  gooseneckCoverDirection?: boolean | number;
  batteryPcbSolderingPoint?: boolean | number;
  buttonPcbaSolderingPoint?: boolean | number;
  glueCheckPoint?: boolean | number;
  macIdSticker?: boolean | number;
  speakerWire?: boolean | number;
  powerOnCheck?: boolean | number;
  speakerCheck?: boolean | number;
  ledCheck?: boolean | number;
  powerOffCheck?: boolean | number;
  createdAt?: string;
  updatedAt?: string;
}

function DashBoardIqcData() {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(getCurrentUserRole());
  const canDeleteIqc = hasPermission("canDeleteIqc", userRole);

  useEffect(() => {
    const handleRoleChange = (e: any) => {
      const fresh = e?.detail?.role ? normalizeRole(e.detail.role) : getCurrentUserRole();
      setUserRole(fresh);
    };
    window.addEventListener("roleChanged", handleRoleChange);
    window.addEventListener("storage", handleRoleChange);
    return () => {
      window.removeEventListener("roleChanged", handleRoleChange);
      window.removeEventListener("storage", handleRoleChange);
    };
  }, []);

  // Helper to retrieve currently logged-in username
  const getLoggedInUser = useCallback((): string => {
    const storedName = localStorage.getItem("userName");
    if (storedName && storedName.trim()) return storedName.trim();
    const storedEmail = localStorage.getItem("userEmail");
    if (storedEmail && storedEmail.trim()) {
      return storedEmail.split("@")[0];
    }
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const parts = token.split(".");
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          if (payload.name) return payload.name;
          if (payload.username) return payload.username;
          if (payload.emailId) return payload.emailId.split("@")[0];
        }
      } catch (e) {}
    }
    return "Inspector";
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("auth");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userName");
    localStorage.removeItem("userEmail");
    localStorage.clear();
    navigate("/login", { replace: true });
  };

  // =========================================================================
  // 1. IQC DATA STATE
  // =========================================================================
  const [iqcList, setIqcList] = useState<IqcRecord[]>([]);
  const [iqcLoading, setIqcLoading] = useState<boolean>(false);
  const [iqcSearch, setIqcSearch] = useState<string>("");
  const [iqcStatusFilter, setIqcStatusFilter] = useState<string>("ALL");
  const [iqcPage, setIqcPage] = useState<number>(1);
  const iqcItemsPerPage = 15;

  // Single IQC Manual Add Modal State (All Checkpoints)
  const [isAddIqcModalOpen, setIsAddIqcModalOpen] = useState<boolean>(false);
  const [newIqcMacId, setNewIqcMacId] = useState("");
  const [newIqcModel, setNewIqcModel] = useState("STUDDS RYDIO");
  const [newIqcNotes, setNewIqcNotes] = useState("");
  const [newIqcInspector, setNewIqcInspector] = useState("");

  // 6 Visual Checkpoints (default to null for manual selection)
  const [newIqcGooseneck, setNewIqcGooseneck] = useState<boolean | null>(null);
  const [newIqcBatteryPcb, setNewIqcBatteryPcb] = useState<boolean | null>(null);
  const [newIqcButtonPcba, setNewIqcButtonPcba] = useState<boolean | null>(null);
  const [newIqcGlue, setNewIqcGlue] = useState<boolean | null>(null);
  const [newIqcMacSticker, setNewIqcMacSticker] = useState<boolean | null>(null);
  const [newIqcSpeakerWire, setNewIqcSpeakerWire] = useState<boolean | null>(null);

  // 4 Functional Checkpoints (default to null for manual selection)
  const [newIqcPowerOn, setNewIqcPowerOn] = useState<boolean | null>(null);
  const [newIqcSpeaker, setNewIqcSpeaker] = useState<boolean | null>(null);
  const [newIqcLed, setNewIqcLed] = useState<boolean | null>(null);
  const [newIqcPowerOff, setNewIqcPowerOff] = useState<boolean | null>(null);

  // Overall Statuses
  const [newIqcVisualStatus, setNewIqcVisualStatus] = useState<"PASS" | "FAIL" | "PENDING">("PENDING");
  const [newIqcFunctionalStatus, setNewIqcFunctionalStatus] = useState<"PASS" | "FAIL" | "PENDING">("PENDING");
  const [newIqcFinalStatus, setNewIqcFinalStatus] = useState<"PASSED" | "REJECTED" | "PENDING">("PENDING");
  const [isSubmittingIqc, setIsSubmittingIqc] = useState(false);

  // Visual Inspection Reference Standards & Lightbox
  const visualRefImages = useVisualReferenceImages();
  const [isRefLightboxOpen, setIsRefLightboxOpen] = useState<boolean>(false);
  const [activeRefKey, setActiveRefKey] = useState<string>("gooseneck");

  // Detailed Inspect Modal for IQC Record
  const [inspectIqcItem, setInspectIqcItem] = useState<IqcRecord | null>(null);

  // IQC Delete State
  const [deleteIqcCandidate, setDeleteIqcCandidate] = useState<IqcRecord | null>(null);
  const [isDeletingIqc, setIsDeletingIqc] = useState<boolean>(false);

  // IQC Bulk Upload State
  const [isIqcUploadModalOpen, setIsIqcUploadModalOpen] = useState(false);
  const [iqcUploadFile, setIqcUploadFile] = useState<File | null>(null);
  const [iqcUploadPreview, setIqcUploadPreview] = useState<any[]>([]);
  const [isIqcUploading, setIsIqcUploading] = useState(false);
  const iqcFileInputRef = useRef<HTMLInputElement | null>(null);

  // Snackbar State
  const [snack, setSnack] = useState({ open: false, message: "", type: "info" });
  const showSnackbar = (message: string, type = "info") => {
    setSnack({ open: true, message, type });
  };

  // Dedicated Top-Level Error Popup State (Visible on top of everything)
  const [errorPopup, setErrorPopup] = useState<{
    open: boolean;
    title: string;
    message: string;
    hint?: string;
  }>({
    open: false,
    title: "Validation Error",
    message: "",
  });

  const showErrorModal = (message: string, title = "Validation Error", hint?: string) => {
    setErrorPopup({ open: true, title, message, hint });
  };

  // =========================================================================
  // 2. FETCH IQC LIST
  // =========================================================================
  const fetchIqcList = useCallback(async () => {
    setIqcLoading(true);
    try {
      const res = await apiFetch("/master-mac/iqc/all", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (res.ok) {
        const data = await res.json();
        setIqcList(data.iqcRecords || []);
      } else {
        showSnackbar("Failed to fetch IQC records", "error");
      }
    } catch (err) {
      console.error("IQC list fetch error:", err);
      showSnackbar("Server connection error while fetching IQC records", "error");
    } finally {
      setIqcLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIqcList();
  }, [fetchIqcList]);

  // =========================================================================
  // 3. STATISTICS & FILTERING
  // =========================================================================
  const iqcStats = useMemo(() => {
    let passed = 0;
    let rejected = 0;
    let pending = 0;
    iqcList.forEach((item) => {
      if (item.finalStatus === "PASSED" || item.overall_status === "PASS") passed++;
      else if (item.finalStatus === "REJECTED" || item.overall_status === "FAIL") rejected++;
      else pending++;
    });
    return {
      total: iqcList.length,
      passed,
      rejected,
      pending,
    };
  }, [iqcList]);

  const filteredIqcList = useMemo(() => {
    return iqcList.filter((item) => {
      if (iqcStatusFilter !== "ALL") {
        if (iqcStatusFilter === "PASSED" && item.finalStatus !== "PASSED" && item.overall_status !== "PASS") {
          return false;
        }
        if (iqcStatusFilter === "REJECTED" && item.finalStatus !== "REJECTED" && item.overall_status !== "FAIL") {
          return false;
        }
        if (iqcStatusFilter === "PENDING" && (item.finalStatus === "PASSED" || item.finalStatus === "REJECTED")) {
          return false;
        }
      }
      const q = iqcSearch.toLowerCase().trim();
      return (
        !q ||
        item.macId.toLowerCase().includes(q) ||
        (item.modelName || "").toLowerCase().includes(q) ||
        (item.finalStatus || "").toLowerCase().includes(q) ||
        (item.overall_status || "").toLowerCase().includes(q) ||
        (item.visualQcName || "").toLowerCase().includes(q) ||
        (item.functionalQcName || "").toLowerCase().includes(q) ||
        (item.inspector_name || "").toLowerCase().includes(q) ||
        (item.qcInspector || "").toLowerCase().includes(q) ||
        (item.notes || "").toLowerCase().includes(q)
      );
    });
  }, [iqcList, iqcSearch, iqcStatusFilter]);

  const totalIqcPages = Math.ceil(filteredIqcList.length / iqcItemsPerPage) || 1;
  const paginatedIqcList = useMemo(() => {
    const start = (iqcPage - 1) * iqcItemsPerPage;
    return filteredIqcList.slice(start, start + iqcItemsPerPage);
  }, [filteredIqcList, iqcPage, iqcItemsPerPage]);

  // =========================================================================
  // 4. ACTION HANDLERS
  // =========================================================================

  // Helper to mark all 10 checkpoints PASS
  const handlePassAllCheckpoints = () => {
    setNewIqcGooseneck(true);
    setNewIqcBatteryPcb(true);
    setNewIqcButtonPcba(true);
    setNewIqcGlue(true);
    setNewIqcMacSticker(true);
    setNewIqcSpeakerWire(true);
    setNewIqcPowerOn(true);
    setNewIqcSpeaker(true);
    setNewIqcLed(true);
    setNewIqcPowerOff(true);
    setNewIqcVisualStatus("PASS");
    setNewIqcFunctionalStatus("PASS");
    setNewIqcFinalStatus("PASSED");
  };

  // Helper to reset all 10 checkpoints to unselected (null)
  const handleResetCheckpoints = () => {
    setNewIqcGooseneck(null);
    setNewIqcBatteryPcb(null);
    setNewIqcButtonPcba(null);
    setNewIqcGlue(null);
    setNewIqcMacSticker(null);
    setNewIqcSpeakerWire(null);
    setNewIqcPowerOn(null);
    setNewIqcSpeaker(null);
    setNewIqcLed(null);
    setNewIqcPowerOff(null);
    setNewIqcVisualStatus("PENDING");
    setNewIqcFunctionalStatus("PENDING");
    setNewIqcFinalStatus("PENDING");
  };

  // Helper to toggle individual IQC checkpoint (PASS / FAIL / unselect) and recalculate statuses
  const toggleIqcCheckpoint = (name: string, targetVal: boolean) => {
    let vG = newIqcGooseneck,
      vB = newIqcBatteryPcb,
      vBu = newIqcButtonPcba,
      vGl = newIqcGlue,
      vM = newIqcMacSticker,
      vS = newIqcSpeakerWire;
    let fP = newIqcPowerOn,
      fSp = newIqcSpeaker,
      fL = newIqcLed,
      fPo = newIqcPowerOff;

    const calcNewVal = (current: boolean | null): boolean | null => {
      return current === targetVal ? null : targetVal;
    };

    if (name === "gooseneck") {
      vG = calcNewVal(newIqcGooseneck);
      setNewIqcGooseneck(vG);
    }
    if (name === "batteryPcb") {
      vB = calcNewVal(newIqcBatteryPcb);
      setNewIqcBatteryPcb(vB);
    }
    if (name === "buttonPcba") {
      vBu = calcNewVal(newIqcButtonPcba);
      setNewIqcButtonPcba(vBu);
    }
    if (name === "glue") {
      vGl = calcNewVal(newIqcGlue);
      setNewIqcGlue(vGl);
    }
    if (name === "macSticker") {
      vM = calcNewVal(newIqcMacSticker);
      setNewIqcMacSticker(vM);
    }
    if (name === "speakerWire") {
      vS = calcNewVal(newIqcSpeakerWire);
      setNewIqcSpeakerWire(vS);
    }

    if (name === "powerOn") {
      fP = calcNewVal(newIqcPowerOn);
      setNewIqcPowerOn(fP);
    }
    if (name === "speaker") {
      fSp = calcNewVal(newIqcSpeaker);
      setNewIqcSpeaker(fSp);
    }
    if (name === "led") {
      fL = calcNewVal(newIqcLed);
      setNewIqcLed(fL);
    }
    if (name === "powerOff") {
      fPo = calcNewVal(newIqcPowerOff);
      setNewIqcPowerOff(fPo);
    }

    const visualList = [vG, vB, vBu, vGl, vM, vS];
    const funcList = [fP, fSp, fL, fPo];

    // Recalculate Visual Status
    if (visualList.some((v) => v === false)) {
      setNewIqcVisualStatus("FAIL");
    } else if (visualList.every((v) => v === true)) {
      setNewIqcVisualStatus("PASS");
    } else {
      setNewIqcVisualStatus("PENDING");
    }

    // Recalculate Functional Status
    if (funcList.some((f) => f === false)) {
      setNewIqcFunctionalStatus("FAIL");
    } else if (funcList.every((f) => f === true)) {
      setNewIqcFunctionalStatus("PASS");
    } else {
      setNewIqcFunctionalStatus("PENDING");
    }

    // Recalculate Overall / Final Status
    const all10 = [...visualList, ...funcList];
    if (all10.some((x) => x === false)) {
      setNewIqcFinalStatus("REJECTED");
    } else if (all10.every((x) => x === true)) {
      setNewIqcFinalStatus("PASSED");
    } else {
      setNewIqcFinalStatus("PENDING");
    }
  };

  // Handle Add Single IQC Record Manually
  const handleAddSingleIqc = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newIqcMacId || !newIqcMacId.trim()) {
      showSnackbar("MAC ID is required", "error");
      return;
    }

    // Enforce manual inspection: all 10 checkpoints must be explicitly PASS or FAIL
    const checkpoints = [
      { name: "Gooseneck Cover Direction", val: newIqcGooseneck },
      { name: "Battery & Battery PCB Soldering Point", val: newIqcBatteryPcb },
      { name: "Button PCBA Soldering Check Point", val: newIqcButtonPcba },
      { name: "Glue Check Point", val: newIqcGlue },
      { name: "MAC ID Sticker", val: newIqcMacSticker },
      { name: "Speaker Wire", val: newIqcSpeakerWire },
      { name: "Power ON Check", val: newIqcPowerOn },
      { name: "Speaker Check", val: newIqcSpeaker },
      { name: "LED Check", val: newIqcLed },
      { name: "Power OFF Check", val: newIqcPowerOff },
    ];
    const unselected = checkpoints.filter((c) => c.val === null);
    if (unselected.length > 0) {
      showSnackbar(
        `Please manually inspect and select PASS or FAIL for all 10 inspection points (${unselected.length} unselected).`,
        "error"
      );
      return;
    }

    setIsSubmittingIqc(true);
    try {
      const inspector = newIqcInspector.trim() || getLoggedInUser();
      const finalVisual = newIqcVisualStatus === "PASS" ? "PASS" : "FAIL";
      const finalFunc = newIqcFunctionalStatus === "PASS" ? "PASS" : "FAIL";
      const finalOverall =
        newIqcFinalStatus === "PASSED" || (finalVisual === "PASS" && finalFunc === "PASS")
          ? "PASSED"
          : "REJECTED";
      const overallStatus =
        finalVisual === "PASS" && finalFunc === "PASS" && finalOverall === "PASSED"
          ? "PASS"
          : "FAIL";

      const res = await apiFetch("/master-mac/iqc/add", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          macId: formatMacWithColon(newIqcMacId.trim().toUpperCase()),
          modelName: "STUDDS RYDIO",
          visualQcStatus: finalVisual,
          functionalQcStatus: finalFunc,
          finalStatus: finalOverall,
          overall_status: overallStatus,
          visualQcName: inspector,
          functionalQcName: inspector,
          qcInspector: inspector,
          inspector_name: inspector,
          loggedInUser: inspector,
          notes: newIqcNotes.trim() || undefined,
          gooseneckCoverDirection: newIqcGooseneck ? 1 : 0,
          batteryPcbSolderingPoint: newIqcBatteryPcb ? 1 : 0,
          buttonPcbaSolderingPoint: newIqcButtonPcba ? 1 : 0,
          glueCheckPoint: newIqcGlue ? 1 : 0,
          macIdSticker: newIqcMacSticker ? 1 : 0,
          speakerWire: newIqcSpeakerWire ? 1 : 0,
          powerOnCheck: newIqcPowerOn ? 1 : 0,
          speakerCheck: newIqcSpeaker ? 1 : 0,
          ledCheck: newIqcLed ? 1 : 0,
          powerOffCheck: newIqcPowerOff ? 1 : 0,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        showSnackbar(
          `IQC Record '${formatMacWithColon(newIqcMacId)}' registered with Inspector '${inspector}'!`,
          "success"
        );
        setNewIqcMacId("");
        setNewIqcModel("STUDDS RYDIO");
        setNewIqcNotes("");
        handleResetCheckpoints();
        setIsAddIqcModalOpen(false);
        fetchIqcList();
      } else {
        const errorMsg = data.message || "Failed to add IQC record";
        showSnackbar(errorMsg, "error");
        showErrorModal(
          errorMsg,
          "Master Mac Validation Error",
          errorMsg.toLowerCase().includes("master mac")
            ? "This MAC ID is not present in the Master Mac DB list. Please register the MAC ID under 'Master Mac ID List' before performing IQC Inspection."
            : undefined
        );
      }
    } catch (err: any) {
      const errorMsg = err.message || "Server error adding IQC record";
      showSnackbar(errorMsg, "error");
      showErrorModal(errorMsg, "IQC Server Error");
    } finally {
      setIsSubmittingIqc(false);
    }
  };

  // Delete IQC Record
  const confirmDeleteIqc = async () => {
    if (!deleteIqcCandidate) return;
    setIsDeletingIqc(true);
    try {
      const res = await apiFetch(`/master-mac/iqc/${deleteIqcCandidate.id}`, { method: "DELETE" });
      const data = await res.json();
      if (res.ok) {
        showSnackbar(`IQC record for '${deleteIqcCandidate.macId}' removed`, "success");
        setDeleteIqcCandidate(null);
        fetchIqcList();
      } else {
        showSnackbar(data.message || "Failed to delete IQC record", "error");
      }
    } catch (err: any) {
      showSnackbar(err.message || "Server error deleting IQC record", "error");
    } finally {
      setIsDeletingIqc(false);
    }
  };

  // IQC File Select & Preview Handler (Modal)
  const handleIqcFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIqcUploadFile(file);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const jsonData = XLSX.utils.sheet_to_json(ws);
        const formattedPreview = (jsonData || []).slice(0, 5).map((row: any) => {
          const rawMac =
            row.macId ||
            row.MacId ||
            row["MAC ID"] ||
            row["MAC Id"] ||
            row["mac id"] ||
            row["Mac ID"] ||
            row.MAC ||
            row.mac;
          return {
            ...row,
            ...(rawMac ? { macId: formatMacWithColon(rawMac), "MAC ID": formatMacWithColon(rawMac) } : {}),
          };
        });
        setIqcUploadPreview(formattedPreview);
      } catch (err) {
        console.error("Preview parsing error:", err);
      }
    };
    reader.readAsBinaryString(file);
  };

  // Confirm & Submit Bulk Upload IQC Data from Modal
  const handleConfirmIqcUpload = async () => {
    if (!iqcUploadFile) {
      showSnackbar("Please select an IQC CSV or Excel file to upload", "error");
      return;
    }

    setIsIqcUploading(true);
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const jsonData: any[] = XLSX.utils.sheet_to_json(ws);

        if (!jsonData || jsonData.length === 0) {
          showSnackbar("IQC file contains no data rows", "error");
          setIsIqcUploading(false);
          return;
        }

        const formattedJsonData = jsonData.map((row: any) => {
          const rawMac =
            row.macId ||
            row.MacId ||
            row["MAC ID"] ||
            row["MAC Id"] ||
            row["mac id"] ||
            row["Mac ID"] ||
            row.MAC ||
            row.mac;
          if (rawMac) {
            const formatted = formatMacWithColon(rawMac);
            return {
              ...row,
              macId: formatted,
              "MAC ID": formatted,
            };
          }
          return row;
        });

        const inspector = getLoggedInUser();
        const res = await apiFetch("/master-mac/iqc/upload", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            jsonData: formattedJsonData,
            loggedInUser: inspector,
            qcInspector: inspector,
          }),
        });

        const data = await res.json();
        if (res.ok) {
          showSnackbar(data.message || "IQC records uploaded successfully!", "success");
          setIsIqcUploadModalOpen(false);
          setIqcUploadFile(null);
          setIqcUploadPreview([]);
          fetchIqcList();
        } else {
          const errorMsg = data.message || "Failed to upload IQC records";
          showSnackbar(errorMsg, "error");
          showErrorModal(
            errorMsg,
            "IQC Bulk Upload Validation Error",
            errorMsg.toLowerCase().includes("master mac")
              ? "One or more MAC IDs in this file are not registered in the Master Mac DB. Only MAC IDs registered in Master Mac ID List can undergo IQC inspection."
              : undefined
          );
        }
      } catch (err: any) {
        const errorMsg = err.message || "Error processing IQC upload";
        showSnackbar(errorMsg, "error");
        showErrorModal(errorMsg, "IQC Bulk Upload Error");
      } finally {
        setIsIqcUploading(false);
        if (iqcFileInputRef.current) iqcFileInputRef.current.value = "";
      }
    };
    reader.readAsBinaryString(iqcUploadFile);
  };

  // Sample IQC Inspection Data with Visual and Functional Checkpoints
  const sampleIqcRows = [
    {
      "MAC ID": "00:1A:2B:3C:50:01",
      "Model Name": "RYDIO",
      "Gooseneck Cover Direction": "PASS",
      "Battery & Battery PCB Soldering Point": "PASS",
      "Button PCBA Soldering Check Point": "PASS",
      "Glue Check Point": "PASS",
      "MAC ID Sticker": "PASS",
      "Speaker Wire": "PASS",
      "Power ON": "PASS",
      "Speaker Check": "PASS",
      "LED Check": "PASS",
      "Power OFF": "PASS",
      "Visual QC Status": "PASS",
      "Functional QC Status": "PASS",
      "Final Status": "PASSED",
      "Notes": "Full inspection verified - all visual and functional tests passed",
    },
    {
      "MAC ID": "00:1A:2B:3C:50:02",
      "Model Name": "Thunder D1",
      "Gooseneck Cover Direction": "PASS",
      "Battery & Battery PCB Soldering Point": "PASS",
      "Button PCBA Soldering Check Point": "FAIL",
      "Glue Check Point": "PASS",
      "MAC ID Sticker": "PASS",
      "Speaker Wire": "PASS",
      "Power ON": "PASS",
      "Speaker Check": "PASS",
      "LED Check": "PASS",
      "Power OFF": "PASS",
      "Visual QC Status": "FAIL",
      "Functional QC Status": "PASS",
      "Final Status": "REJECTED",
      "Notes": "Defective solder joint on button PCBA point",
    },
    {
      "MAC ID": "00:1A:2B:3C:50:03",
      "Model Name": "Ninja Elite",
      "Gooseneck Cover Direction": "PASS",
      "Battery & Battery PCB Soldering Point": "PASS",
      "Button PCBA Soldering Check Point": "PASS",
      "Glue Check Point": "PASS",
      "MAC ID Sticker": "PASS",
      "Speaker Wire": "PASS",
      "Power ON": "PASS",
      "Speaker Check": "PASS",
      "LED Check": "PASS",
      "Power OFF": "PASS",
      "Visual QC Status": "PASS",
      "Functional QC Status": "PASS",
      "Final Status": "PASSED",
      "Notes": "Batch 4 line clearance passed",
    },
  ];

  // Download Sample IQC CSV
  const downloadIqcSampleCsv = () => {
    const headers = Object.keys(sampleIqcRows[0]);
    const csvContent =
      "\uFEFF" +
      [
        headers.join(","),
        ...sampleIqcRows.map((row) =>
          headers
            .map((h) => {
              const val = String((row as any)[h] ?? "");
              return `"${val.replace(/"/g, '""')}"`;
            })
            .join(",")
        ),
      ].join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "IQC_Inspection_Sample_Template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showSnackbar("IQC Inspection Sample CSV downloaded successfully!", "success");
  };

  // Download Sample IQC Excel (.xlsx)
  const downloadIqcSampleExcel = () => {
    const ws = XLSX.utils.json_to_sheet(sampleIqcRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "IQC_Inspection_Template");
    XLSX.writeFile(wb, "IQC_Inspection_Sample_Template.xlsx");
    showSnackbar("IQC Inspection Sample Excel downloaded successfully!", "success");
  };

  // Export IQC Table to Excel
  const exportIqcToExcel = () => {
    if (filteredIqcList.length === 0) {
      showSnackbar("No IQC records to export", "info");
      return;
    }

    const rowsToExport = filteredIqcList.map((item, idx) => ({
      "SR NO": idx + 1,
      "MAC ID": item.macId,
      "MODEL NAME": item.modelName || "—",
      "QC INSPECTOR":
        item.inspector_name ||
        item.qcInspector ||
        item.visualQcName ||
        item.functionalQcName ||
        "Unassigned",
      "VISUAL QC INSPECTOR": item.visualQcName || "—",
      "FUNCTIONAL QC INSPECTOR": item.functionalQcName || "—",
      "GOOSENECK COVER DIRECTION": Boolean(item.gooseneckCoverDirection) ? "PASS" : "FAIL",
      "BATTERY & PCB SOLDERING": Boolean(item.batteryPcbSolderingPoint) ? "PASS" : "FAIL",
      "BUTTON PCBA SOLDERING": Boolean(item.buttonPcbaSolderingPoint) ? "PASS" : "FAIL",
      "GLUE CHECK POINT": Boolean(item.glueCheckPoint) ? "PASS" : "FAIL",
      "MAC ID STICKER": Boolean(item.macIdSticker) ? "PASS" : "FAIL",
      "SPEAKER WIRE": Boolean(item.speakerWire) ? "PASS" : "FAIL",
      "POWER ON": Boolean(item.powerOnCheck) ? "PASS" : "FAIL",
      "SPEAKER CHECK": Boolean(item.speakerCheck) ? "PASS" : "FAIL",
      "LED CHECK": Boolean(item.ledCheck) ? "PASS" : "FAIL",
      "POWER OFF": Boolean(item.powerOffCheck) ? "PASS" : "FAIL",
      "VISUAL QC STATUS": item.visualQcStatus || "—",
      "FUNCTIONAL QC STATUS": item.functionalQcStatus || "—",
      "FINAL STATUS": item.finalStatus || "—",
      "OVER ALL STATUS": item.overall_status || (item.finalStatus === "PASSED" ? "PASS" : "FAIL"),
      "NOTES": item.notes || "—",
      "INSPECTION DATE": item.createdAt ? new Date(item.createdAt).toLocaleString("en-GB") : "—",
    }));

    const ws = XLSX.utils.json_to_sheet(rowsToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "IQC_List");
    XLSX.writeFile(
      wb,
      `IQC_List_${new Date().toISOString().split("T")[0]}.xlsx`
    );
    showSnackbar("IQC List exported to Excel successfully!", "success");
  };

  return (
    <div className="master-mac-container">
      {/* Top Header matching standard dashboard screen */}
      <div className="user-list-header master-mac-header">
        <div className="header-title-section">
          <h2>IQC List & Inspection Checklist</h2>
          <p>
            Incoming Quality Control (IQC) Hardware, Visual & Functional Checkpoints Validation
          </p>
        </div>

        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            className="header-upload-btn iqc"
            onClick={() => setIsIqcUploadModalOpen(true)}
            title="Upload IQC Quality Inspection data via CSV or Excel"
          >
            <MdCloudUpload size={18} />
            <span>Upload IQC (CSV / Excel)</span>
          </button>

          <button
            type="button"
            className="user-btn-secondary profile-link-btn"
            onClick={() => navigate("/dashboard/profile")}
          >
            <MdPerson size={18} />
            <span>My Profile</span>
          </button>

          <div className="icons" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <span
              className="round-header-icon"
              onClick={() => {
                fetchIqcList();
                showSnackbar("IQC Quality records refreshed", "info");
              }}
              title="Refresh IQC Data"
            >
              <MdRefresh size={20} color="#4b5563" />
            </span>
            <span
              className="round-header-icon logout"
              onClick={handleLogout}
              title="Logout"
            >
              <MdLogout size={18} color="#dc2626" />
            </span>
          </div>
        </div>
      </div>

      {/* IQC Summary KPI Cards */}
      <div className="kpi-cards-grid" style={{ marginBottom: "16px" }}>
        <div
          className={`kpi-card master clickable ${iqcStatusFilter === "ALL" ? "active-filter" : ""}`}
          onClick={() => {
            setIqcStatusFilter("ALL");
            setIqcPage(1);
          }}
          title="Click to view all IQC inspection records"
        >
          <div className="kpi-icon-wrap">
            <MdFactCheck />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Total IQC Records</span>
            <span className="kpi-value">{iqcStats.total}</span>
            <span className="kpi-sub">Total batch inspections</span>
          </div>
        </div>

        <div
          className={`kpi-card completed clickable ${iqcStatusFilter === "PASSED" ? "active-filter" : ""}`}
          onClick={() => {
            setIqcStatusFilter("PASSED");
            setIqcPage(1);
          }}
          title="Click to view passed IQC records"
        >
          <div className="kpi-icon-wrap">
            <MdCheckCircle />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Passed Inspections</span>
            <span className="kpi-value" style={{ color: "#059669" }}>
              {iqcStats.passed}
            </span>
            <span className="kpi-sub">Visual & functional passed</span>
          </div>
        </div>

        <div
          className={`kpi-card unverified clickable ${iqcStatusFilter === "REJECTED" ? "active-filter" : ""}`}
          onClick={() => {
            setIqcStatusFilter("REJECTED");
            setIqcPage(1);
          }}
          title="Click to view rejected IQC records"
        >
          <div className="kpi-icon-wrap">
            <MdWarningAmber />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Rejected Units</span>
            <span className="kpi-value" style={{ color: "#dc2626" }}>
              {iqcStats.rejected}
            </span>
            <span className="kpi-sub">Failed quality checkpoints</span>
          </div>
        </div>

        <div
          className={`kpi-card pending clickable ${iqcStatusFilter === "PENDING" ? "active-filter" : ""}`}
          onClick={() => {
            setIqcStatusFilter("PENDING");
            setIqcPage(1);
          }}
          title="Click to view pending IQC records"
        >
          <div className="kpi-icon-wrap">
            <MdPending />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Pending Inspections</span>
            <span className="kpi-value" style={{ color: "#d97706" }}>
              {iqcStats.pending}
            </span>
            <span className="kpi-sub">Awaiting full verification</span>
          </div>
        </div>
      </div>

      {/* IQC Filter Pills */}
      <div className="filter-pills-row">
        <span
          className={`filter-pill ${iqcStatusFilter === "ALL" ? "active" : ""}`}
          onClick={() => {
            setIqcStatusFilter("ALL");
            setIqcPage(1);
          }}
        >
          All Inspections <span className="pill-count">{iqcStats.total}</span>
        </span>

        <span
          className={`filter-pill ${iqcStatusFilter === "PASSED" ? "active" : ""}`}
          onClick={() => {
            setIqcStatusFilter("PASSED");
            setIqcPage(1);
          }}
        >
          ✓ Passed <span className="pill-count">{iqcStats.passed}</span>
        </span>

        <span
          className={`filter-pill ${iqcStatusFilter === "REJECTED" ? "active" : ""}`}
          onClick={() => {
            setIqcStatusFilter("REJECTED");
            setIqcPage(1);
          }}
        >
          ✕ Rejected <span className="pill-count">{iqcStats.rejected}</span>
        </span>

        <span
          className={`filter-pill ${iqcStatusFilter === "PENDING" ? "active" : ""}`}
          onClick={() => {
            setIqcStatusFilter("PENDING");
            setIqcPage(1);
          }}
        >
          ⏳ Pending <span className="pill-count">{iqcStats.pending}</span>
        </span>
      </div>

      {/* Toolbar */}
      <div className="mac-toolbar">
        <div className="toolbar-left">
          <div className="toolbar-search">
            <MdSearch className="search-icon" size={18} />
            <input
              type="text"
              placeholder="Search IQC records by MAC, model, status, inspector..."
              value={iqcSearch}
              onChange={(e) => {
                setIqcSearch(e.target.value);
                setIqcPage(1);
              }}
            />
          </div>

          {(iqcSearch || iqcStatusFilter !== "ALL") && (
            <button
              type="button"
              className="toolbar-btn secondary"
              onClick={() => {
                setIqcSearch("");
                setIqcStatusFilter("ALL");
                setIqcPage(1);
              }}
            >
              Clear Filters
            </button>
          )}
        </div>

        <div className="toolbar-right">
          <span style={{ fontSize: "13px", color: "#6b7280" }}>
            Total IQC Records: <strong>{iqcList.length}</strong>
          </span>

          {/* Add Single IQC Button */}
          <button
            type="button"
            className="toolbar-btn primary"
            onClick={() => {
              setNewIqcInspector(getLoggedInUser());
              handleResetCheckpoints();
              setIsAddIqcModalOpen(true);
            }}
            title="Add a single IQC record manually along with all checkpoints"
            style={{
              background: "#e60000",
              borderColor: "#e60000",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontWeight: 700,
            }}
          >
            <MdAdd size={18} />
            <span>Add IQC Manually</span>
          </button>

          {/* Upload IQC Button */}
          <button
            type="button"
            className="toolbar-btn secondary"
            onClick={() => setIsIqcUploadModalOpen(true)}
            title="Upload batch of IQC test results via CSV or Excel file"
          >
            <MdCloudUpload size={18} />
            <span>Upload IQC (CSV / Excel)</span>
          </button>

          {/* Export IQC to Excel */}
          <button
            type="button"
            className="toolbar-btn export"
            onClick={exportIqcToExcel}
            title="Export IQC quality inspections to Excel"
          >
            <MdFileDownload size={18} />
            <span>Export to Excel</span>
          </button>

          <button type="button" className="toolbar-btn secondary" onClick={fetchIqcList}>
            <MdRefresh size={18} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* IQC Table Card */}
      <div className="master-table-card">
        <table className="master-table">
          <thead>
            <tr>
              <th style={{ width: "45px", textAlign: "center" }}>SR</th>
              <th>IQC MAC ID</th>
              <th>MODEL NAME</th>
              <th>QC INSPECTOR</th>
              <th>CHECKPOINTS</th>
              <th>VISUAL QC</th>
              <th>FUNCTIONAL QC</th>
              <th>FINAL STATUS</th>
              <th>OVER ALL STATUS</th>
              <th>NOTES</th>
              <th>INSPECTION DATE</th>
              <th style={{ textAlign: "center", width: "100px" }}>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {iqcLoading ? (
              <tr>
                <td colSpan={12} style={{ textAlign: "center", padding: "40px" }}>
                  <p style={{ color: "#6b7280", margin: 0 }}>Loading IQC inspection data...</p>
                </td>
              </tr>
            ) : paginatedIqcList.length === 0 ? (
              <tr>
                <td colSpan={12}>
                  <div className="table-empty-box">
                    <MdFactCheck />
                    <h4
                      style={{
                        background: "transparent",
                        backgroundColor: "transparent",
                        color: "#374151",
                        margin: "0 0 4px 0",
                        padding: 0,
                      }}
                    >
                      No IQC records found
                    </h4>
                    <p style={{ margin: "0 0 14px 0", color: "#64748b" }}>
                      Add inspection records manually with all checkpoints or upload a CSV / Excel batch.
                    </p>
                    <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="toolbar-btn primary"
                        style={{ background: "#e60000", borderColor: "#e60000" }}
                        onClick={() => {
                          setNewIqcInspector(getLoggedInUser());
                          handleResetCheckpoints();
                          setIsAddIqcModalOpen(true);
                        }}
                      >
                        <MdAdd size={16} />
                        <span>Add IQC Manually</span>
                      </button>
                      <button
                        type="button"
                        className="toolbar-btn secondary"
                        onClick={() => setIsIqcUploadModalOpen(true)}
                      >
                        <MdCloudUpload size={16} />
                        <span>Upload IQC (CSV / Excel)</span>
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedIqcList.map((item, idx) => {
                const sr = (iqcPage - 1) * iqcItemsPerPage + idx + 1;
                const vPassed = [
                  item.gooseneckCoverDirection,
                  item.batteryPcbSolderingPoint,
                  item.buttonPcbaSolderingPoint,
                  item.glueCheckPoint,
                  item.macIdSticker,
                  item.speakerWire,
                ].filter(Boolean).length;
                const fPassed = [
                  item.powerOnCheck,
                  item.speakerCheck,
                  item.ledCheck,
                  item.powerOffCheck,
                ].filter(Boolean).length;

                return (
                  <tr key={item.id}>
                    <td style={{ textAlign: "center", color: "#9ca3af", fontWeight: 600 }}>
                      {sr}
                    </td>
                    <td>
                      <span className="mac-code">{item.macId}</span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{item.modelName || "—"}</td>
                    <td>
                      <div style={{ fontSize: "12px", lineHeight: "1.4" }}>
                        {item.visualQcName &&
                        item.functionalQcName &&
                        item.visualQcName === item.functionalQcName ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              color: "#1e293b",
                              fontWeight: 600,
                            }}
                          >
                            <MdPerson size={15} style={{ color: "#0284c7" }} />
                            <span>
                              {item.inspector_name || item.qcInspector || item.visualQcName}
                            </span>
                          </div>
                        ) : (
                          <>
                            {item.visualQcName ? (
                              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <strong style={{ color: "#0369a1" }}>Vis:</strong> {item.visualQcName}
                              </div>
                            ) : null}
                            {item.functionalQcName ? (
                              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <strong style={{ color: "#047857" }}>Func:</strong> {item.functionalQcName}
                              </div>
                            ) : null}
                            {!item.visualQcName && !item.functionalQcName ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  color: "#64748b",
                                }}
                              >
                                <MdPerson size={15} style={{ color: "#94a3b8" }} />
                                <span>
                                  {item.inspector_name || item.qcInspector || "Unassigned"}
                                </span>
                              </div>
                            ) : null}
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <span
                          className={`iqc-badge-pill ${vPassed === 6 ? "pass" : "fail"}`}
                          title="Visual Checkpoints (Gooseneck, PCB Soldering, Button, Glue, MAC Sticker, Speaker Wire)"
                        >
                          Visual: {vPassed}/6 {vPassed === 6 ? "✓" : "⚠"}
                        </span>
                        <span
                          className={`iqc-badge-pill ${fPassed === 4 ? "pass" : "fail"}`}
                          title="Functional Checkpoints (Power ON, Speaker, LED, Power OFF)"
                        >
                          Func: {fPassed}/4 {fPassed === 4 ? "✓" : "⚠"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span
                        style={{
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "11px",
                          fontWeight: 700,
                          background: item.visualQcStatus === "PASS" ? "#ecfdf5" : "#fee2e2",
                          color: item.visualQcStatus === "PASS" ? "#065f46" : "#991b1b",
                        }}
                      >
                        {item.visualQcStatus || "PASS"}
                      </span>
                    </td>
                    <td>
                      <span
                        style={{
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "11px",
                          fontWeight: 700,
                          background:
                            item.functionalQcStatus === "PASS" ? "#ecfdf5" : "#fee2e2",
                          color: item.functionalQcStatus === "PASS" ? "#065f46" : "#991b1b",
                        }}
                      >
                        {item.functionalQcStatus || "PASS"}
                      </span>
                    </td>
                    <td>
                      <span
                        style={{
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "11px",
                          fontWeight: 700,
                          background:
                            item.finalStatus === "PASSED"
                              ? "#ecfdf5"
                              : item.finalStatus === "REJECTED"
                              ? "#fee2e2"
                              : "#fffbeb",
                          color:
                            item.finalStatus === "PASSED"
                              ? "#065f46"
                              : item.finalStatus === "REJECTED"
                              ? "#991b1b"
                              : "#92400e",
                        }}
                      >
                        {item.finalStatus || "PASSED"}
                      </span>
                    </td>
                    <td>
                      <span
                        style={{
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "11px",
                          fontWeight: 700,
                          background:
                            item.overall_status === "PASS" || item.finalStatus === "PASSED"
                              ? "#ecfdf5"
                              : "#fee2e2",
                          color:
                            item.overall_status === "PASS" || item.finalStatus === "PASSED"
                              ? "#065f46"
                              : "#991b1b",
                        }}
                      >
                        {item.overall_status || (item.finalStatus === "PASSED" ? "PASS" : "FAIL")}
                      </span>
                    </td>
                    <td style={{ color: "#6b7280" }}>{item.notes || "—"}</td>
                    <td style={{ fontSize: "12px", color: "#6b7280" }}>
                      {item.createdAt ? new Date(item.createdAt).toLocaleString("en-GB") : "—"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <div style={{ display: "inline-flex", gap: "6px" }}>
                        <button
                          type="button"
                          className="toolbar-btn secondary"
                          style={{
                            padding: "5px 8px",
                            color: "#0284c7",
                            borderColor: "#bae6fd",
                            background: "#f0f9ff",
                          }}
                          title="Inspect complete checkpoint checklist"
                          onClick={() => setInspectIqcItem(item)}
                        >
                          <MdFactCheck size={16} />
                        </button>
                        {canDeleteIqc && (
                          <button
                            type="button"
                            className="toolbar-btn secondary"
                            style={{
                              padding: "5px 8px",
                              color: "#dc2626",
                              borderColor: "#fecaca",
                              background: "#fef2f2",
                            }}
                            title="Delete this IQC record"
                            onClick={() => setDeleteIqcCandidate(item)}
                          >
                            <MdDeleteOutline size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination Controls */}
        {filteredIqcList.length > iqcItemsPerPage && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "14px 20px",
              borderTop: "1px solid #e5e7eb",
              background: "#fafafa",
            }}
          >
            <span style={{ fontSize: "13px", color: "#6b7280" }}>
              Page {iqcPage} of {totalIqcPages} ({filteredIqcList.length} items)
            </span>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                type="button"
                className="toolbar-btn secondary"
                disabled={iqcPage === 1}
                onClick={() => setIqcPage((p) => Math.max(p - 1, 1))}
              >
                Previous
              </button>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "0 12px",
                  fontWeight: 700,
                  fontSize: "13px",
                }}
              >
                {iqcPage}
              </span>
              <button
                type="button"
                className="toolbar-btn secondary"
                disabled={iqcPage === totalIqcPages}
                onClick={() => setIqcPage((p) => Math.min(p + 1, totalIqcPages))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* =================================================================== */}
      {/* MODAL 1: ADD SINGLE IQC RECORD MANUALLY (ALL CHECKPOINTS)           */}
      {/* =================================================================== */}
      {isAddIqcModalOpen && (() => {
        const visualPoints = [
          newIqcGooseneck,
          newIqcBatteryPcb,
          newIqcButtonPcba,
          newIqcGlue,
          newIqcMacSticker,
          newIqcSpeakerWire,
        ];
        const visualPassedCount = visualPoints.filter((v) => v === true).length;
        const visualFailedCount = visualPoints.filter((v) => v === false).length;
        const visualSelectedCount = visualPoints.filter((v) => v !== null).length;

        const functionalPoints = [
          newIqcPowerOn,
          newIqcSpeaker,
          newIqcLed,
          newIqcPowerOff,
        ];
        const functionalPassedCount = functionalPoints.filter((f) => f === true).length;
        const functionalFailedCount = functionalPoints.filter((f) => f === false).length;
        const functionalSelectedCount = functionalPoints.filter((f) => f !== null).length;

        const totalSelectedCount = visualSelectedCount + functionalSelectedCount;
        const totalPassedCount = visualPassedCount + functionalPassedCount;

        const isVisualCheckpoint = (name: string) =>
          ["gooseneck", "batteryPcb", "buttonPcba", "glue", "macSticker", "speakerWire"].includes(name);

        const renderCheckpointRow = (label: string, val: boolean | null, name: string) => {
          const isPass = val === true;
          const isFail = val === false;
          const isVisual = isVisualCheckpoint(name);

          return (
            <div
              className={`iqc-item-row ${
                isPass ? "is-pass" : isFail ? "is-fail" : "is-unselected"
              }`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                borderRadius: "8px",
                border: isPass
                  ? "1px solid #bbf7d0"
                  : isFail
                  ? "1px solid #fecaca"
                  : "1px solid #e2e8f0",
                background: isPass ? "#f0fdf4" : isFail ? "#fef2f2" : "#ffffff",
                transition: "all 0.15s ease",
                gap: "8px",
              }}
            >
              <div className="iqc-item-info" style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
                <span
                  className={`iqc-item-dot ${isPass ? "pass" : isFail ? "fail" : "unselected"}`}
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: isPass ? "#16a34a" : isFail ? "#dc2626" : "#94a3b8",
                    flexShrink: 0,
                  }}
                />
                <span
                  className="iqc-item-name"
                  style={{
                    fontSize: "12.5px",
                    fontWeight: 600,
                    color: isPass ? "#15803d" : isFail ? "#991b1b" : "#334155",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {label}
                </span>
              </div>

              {isVisual && (
                <IqcCheckpointThumbnail
                  checkpointKey={name}
                  resolvedSrc={visualRefImages[name]}
                  onClick={() => {
                    setActiveRefKey(name);
                    setIsRefLightboxOpen(true);
                  }}
                />
              )}

              <div style={{ display: "flex", gap: "6px", alignItems: "center", flexShrink: 0 }}>
                <label
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "4px 9px",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: 700,
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "all 0.15s ease",
                    background: isPass ? "#dcfce7" : "#f8fafc",
                    color: isPass ? "#15803d" : "#64748b",
                    border: isPass ? "1px solid #86efac" : "1px solid #cbd5e1",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isPass}
                    onChange={() => toggleIqcCheckpoint(name, true)}
                    style={{
                      accentColor: "#16a34a",
                      cursor: "pointer",
                      width: "13px",
                      height: "13px",
                      margin: 0,
                    }}
                  />
                  <span>PASS</span>
                </label>

                <label
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "4px 9px",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: 700,
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "all 0.15s ease",
                    background: isFail ? "#fee2e2" : "#f8fafc",
                    color: isFail ? "#b91c1c" : "#64748b",
                    border: isFail ? "1px solid #fca5a5" : "1px solid #cbd5e1",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isFail}
                    onChange={() => toggleIqcCheckpoint(name, false)}
                    style={{
                      accentColor: "#dc2626",
                      cursor: "pointer",
                      width: "13px",
                      height: "13px",
                      margin: 0,
                    }}
                  />
                  <span>FAIL</span>
                </label>
              </div>
            </div>
          );
        };

        return (
          <div
            className="user-modal-overlay"
            onClick={() => setIsAddIqcModalOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0, 0, 0, 0.65)",
              backdropFilter: "blur(4px)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 99999,
              padding: "20px",
            }}
          >
            <div
              className="user-modal-card"
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#ffffff",
                borderRadius: "14px",
                width: "100%",
                maxWidth: "960px",
                maxHeight: "90vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxShadow: "0 25px 50px rgba(0, 0, 0, 0.35)",
              }}
            >
              {/* Modal Header */}
              <div
                style={{
                  background: "linear-gradient(135deg, #e60000, #b71c1c)",
                  color: "#ffffff",
                  padding: "16px 24px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <MdFactCheck size={26} />
                  <div>
                    <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700 }}>
                      Add IQC Quality Inspection (Manual Entry)
                    </h3>
                    <div style={{ fontSize: "12px", opacity: 0.9, marginTop: "2px" }}>
                      Enter complete hardware, visual & functional quality inspection checkpoints
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAddIqcModalOpen(false)}
                  style={{ background: "transparent", border: "none", color: "#fff", cursor: "pointer" }}
                  title="Close Modal"
                >
                  <MdClose size={24} />
                </button>
              </div>

              {/* Modal Form */}
              <form
                onSubmit={handleAddSingleIqc}
                style={{
                  padding: "20px 24px",
                  overflowY: "auto",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "18px",
                }}
              >
                {/* Section 1: Core Details */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "14px",
                    background: "#f8fafc",
                    padding: "14px 16px",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: 700,
                        marginBottom: "4px",
                        color: "#1e293b",
                      }}
                    >
                      IQC MAC ID <span style={{ color: "#e60000" }}>*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. 00:1A:2B:3C:50:01"
                      value={newIqcMacId}
                      onChange={(e) => setNewIqcMacId(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        fontSize: "13px",
                        fontFamily: "monospace",
                        boxSizing: "border-box",
                        outline: "none",
                      }}
                    />
                    <span style={{ fontSize: "11px", color: "#64748b", marginTop: "4px", display: "block" }}>
                      * Note: Only MAC IDs registered in Master Mac DB List can be inspected.
                    </span>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: 700,
                        marginBottom: "4px",
                        color: "#1e293b",
                      }}
                    >
                      Model Name
                    </label>
                    <input
                      type="text"
                      readOnly
                      disabled
                      value="STUDDS RYDIO"
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        fontSize: "13px",
                        fontWeight: 700,
                        backgroundColor: "#f1f5f9",
                        color: "#334155",
                        cursor: "not-allowed",
                        boxSizing: "border-box",
                        outline: "none",
                      }}
                      title="Model name is fixed to STUDDS RYDIO (Non-editable)"
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: 700,
                        marginBottom: "4px",
                        color: "#1e293b",
                      }}
                    >
                      QC Inspector
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        type="text"
                        value={newIqcInspector || getLoggedInUser()}
                        onChange={(e) => setNewIqcInspector(e.target.value)}
                        placeholder="Inspector Name"
                        style={{
                          width: "100%",
                          padding: "8px 12px 8px 30px",
                          borderRadius: "6px",
                          border: "1px solid #cbd5e1",
                          fontSize: "13px",
                          boxSizing: "border-box",
                          outline: "none",
                          fontWeight: 600,
                          color: "#0369a1",
                          background: "#f0f9ff",
                        }}
                      />
                      <MdPerson
                        size={16}
                        color="#0284c7"
                        style={{ position: "absolute", left: "9px", top: "10px" }}
                      />
                    </div>
                  </div>
                </div>

                {/* Quick Bulk Pass / Reset Checkpoint Buttons */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  <span style={{ fontSize: "13px", fontWeight: 700, color: "#1e293b" }}>
                    Quality Checkpoints Checklist (10 Total Points)
                  </span>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      type="button"
                      onClick={handlePassAllCheckpoints}
                      className="toolbar-btn secondary"
                      style={{
                        padding: "4px 10px",
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "#15803d",
                        borderColor: "#86efac",
                        background: "#dcfce7",
                      }}
                    >
                      ✓ Pass All (10/10)
                    </button>
                    <button
                      type="button"
                      onClick={handleResetCheckpoints}
                      className="toolbar-btn secondary"
                      style={{
                        padding: "4px 10px",
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "#475569",
                        borderColor: "#cbd5e1",
                        background: "#f1f5f9",
                      }}
                    >
                      ✕ Unselect All
                    </button>
                  </div>
                </div>

                {/* Section 2: Visual & Functional Checkpoints Grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
                    gap: "16px",
                  }}
                >
                  {/* Card 1: 6 Visual Inspection Checkpoints */}
                  <div
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: "10px",
                      overflow: "hidden",
                      background: "#ffffff",
                    }}
                  >
                    <div
                      style={{
                        background: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                        padding: "10px 14px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <strong style={{ fontSize: "13px", color: "#1e293b" }}>
                        1. Visual Inspection (6 Points)
                      </strong>
                      <span
                        className={`iqc-badge-pill ${
                          visualFailedCount > 0
                            ? "fail"
                            : visualPassedCount === 6
                            ? "pass"
                            : "pending"
                        }`}
                      >
                        {visualSelectedCount === 0
                          ? "0 / 6 Selected"
                          : visualSelectedCount < 6
                          ? `${visualPassedCount} / 6 Passed (${6 - visualSelectedCount} Pending)`
                          : `${visualPassedCount} / 6 Passed`}
                      </span>
                    </div>

                    <div style={{ padding: "10px 14px", display: "flex", flexDirection: "column", gap: "6px" }}>
                      {renderCheckpointRow("Gooseneck Cover Direction", newIqcGooseneck, "gooseneck")}
                      {renderCheckpointRow("Battery & Battery PCB Soldering Point", newIqcBatteryPcb, "batteryPcb")}
                      {renderCheckpointRow("Button PCBA Soldering Check Point", newIqcButtonPcba, "buttonPcba")}
                      {renderCheckpointRow("Glue Check Point", newIqcGlue, "glue")}
                      {renderCheckpointRow("MAC ID Sticker", newIqcMacSticker, "macSticker")}
                      {renderCheckpointRow("Speaker Wire", newIqcSpeakerWire, "speakerWire")}
                    </div>
                  </div>

                  {/* Card 2: 4 Functional Check Checkpoints */}
                  <div
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: "10px",
                      overflow: "hidden",
                      background: "#ffffff",
                    }}
                  >
                    <div
                      style={{
                        background: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                        padding: "10px 14px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <strong style={{ fontSize: "13px", color: "#1e293b" }}>
                        2. Functional Check (4 Points)
                      </strong>
                      <span
                        className={`iqc-badge-pill ${
                          functionalFailedCount > 0
                            ? "fail"
                            : functionalPassedCount === 4
                            ? "pass"
                            : "pending"
                        }`}
                      >
                        {functionalSelectedCount === 0
                          ? "0 / 4 Selected"
                          : functionalSelectedCount < 4
                          ? `${functionalPassedCount} / 4 Passed (${4 - functionalSelectedCount} Pending)`
                          : `${functionalPassedCount} / 4 Passed`}
                      </span>
                    </div>

                    <div style={{ padding: "10px 14px", display: "flex", flexDirection: "column", gap: "6px" }}>
                      {renderCheckpointRow("Power ON Check", newIqcPowerOn, "powerOn")}
                      {renderCheckpointRow("Speaker Check", newIqcSpeaker, "speaker")}
                      {renderCheckpointRow("LED Check", newIqcLed, "led")}
                      {renderCheckpointRow("Power OFF Check", newIqcPowerOff, "powerOff")}
                    </div>
                  </div>
                </div>

                {/* Section 3: Status Summary Evaluators */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "14px",
                    background: "#f8fafc",
                    padding: "14px 16px",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: 600,
                        marginBottom: "6px",
                        color: "#374151",
                      }}
                    >
                      Visual QC Status
                    </label>
                    <select
                      value={newIqcVisualStatus}
                      onChange={(e: any) => setNewIqcVisualStatus(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        fontSize: "13px",
                        fontWeight: 700,
                        color:
                          newIqcVisualStatus === "PASS"
                            ? "#065f46"
                            : newIqcVisualStatus === "FAIL"
                            ? "#991b1b"
                            : "#475569",
                        background:
                          newIqcVisualStatus === "PASS"
                            ? "#ecfdf5"
                            : newIqcVisualStatus === "FAIL"
                            ? "#fee2e2"
                            : "#f1f5f9",
                      }}
                    >
                      <option value="PENDING">PENDING ({visualSelectedCount}/6 Selected)</option>
                      <option value="PASS">PASS ({visualPassedCount}/6)</option>
                      <option value="FAIL">FAIL</option>
                    </select>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: 600,
                        marginBottom: "6px",
                        color: "#374151",
                      }}
                    >
                      Functional QC Status
                    </label>
                    <select
                      value={newIqcFunctionalStatus}
                      onChange={(e: any) => setNewIqcFunctionalStatus(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        fontSize: "13px",
                        fontWeight: 700,
                        color:
                          newIqcFunctionalStatus === "PASS"
                            ? "#065f46"
                            : newIqcFunctionalStatus === "FAIL"
                            ? "#991b1b"
                            : "#475569",
                        background:
                          newIqcFunctionalStatus === "PASS"
                            ? "#ecfdf5"
                            : newIqcFunctionalStatus === "FAIL"
                            ? "#fee2e2"
                            : "#f1f5f9",
                      }}
                    >
                      <option value="PENDING">PENDING ({functionalSelectedCount}/4 Selected)</option>
                      <option value="PASS">PASS ({functionalPassedCount}/4)</option>
                      <option value="FAIL">FAIL</option>
                    </select>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: 700,
                        marginBottom: "6px",
                        color: "#374151",
                      }}
                    >
                      Over All Status
                    </label>
                    <select
                      value={
                        newIqcFinalStatus === "PASSED"
                          ? "PASS"
                          : newIqcFinalStatus === "REJECTED"
                          ? "FAIL"
                          : "PENDING"
                      }
                      onChange={(e: any) => {
                        const val = e.target.value;
                        setNewIqcFinalStatus(
                          val === "PASS" ? "PASSED" : val === "FAIL" ? "REJECTED" : "PENDING"
                        );
                      }}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        fontSize: "13px",
                        fontWeight: 700,
                        color:
                          newIqcFinalStatus === "PASSED"
                            ? "#065f46"
                            : newIqcFinalStatus === "REJECTED"
                            ? "#991b1b"
                            : "#475569",
                        background:
                          newIqcFinalStatus === "PASSED"
                            ? "#ecfdf5"
                            : newIqcFinalStatus === "REJECTED"
                            ? "#fee2e2"
                            : "#f1f5f9",
                      }}
                    >
                      <option value="PENDING">PENDING ({totalSelectedCount}/10 Selected)</option>
                      <option value="PASS">PASS</option>
                      <option value="FAIL">FAIL</option>
                    </select>
                  </div>
                </div>

                {/* Section 4: Notes / Remarks */}
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: 600,
                      marginBottom: "6px",
                      color: "#374151",
                    }}
                  >
                    Inspection Notes / Remarks
                  </label>
                  <textarea
                    rows={2}
                    placeholder="Enter any production observations, solder joint quality remarks, etc."
                    value={newIqcNotes}
                    onChange={(e) => setNewIqcNotes(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "9px 12px",
                      borderRadius: "8px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      boxSizing: "border-box",
                      outline: "none",
                      resize: "vertical",
                    }}
                  />
                </div>

                {/* Modal Footer */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "12px",
                    paddingTop: "12px",
                    borderTop: "1px solid #e2e8f0",
                  }}
                >
                  <button
                    type="button"
                    className="toolbar-btn secondary"
                    onClick={() => setIsAddIqcModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingIqc}
                    className="toolbar-btn primary"
                    style={{
                      padding: "10px 24px",
                      background: "#e60000",
                      borderColor: "#e60000",
                      fontWeight: 700,
                    }}
                  >
                    {isSubmittingIqc ? "Saving..." : "Save IQC Inspection Record"}
                  </button>
                </div>
              </form>

              {/* High-Resolution Visual Inspection Lightbox Modal */}
              <IqcReferenceLightboxModal
                isOpen={isRefLightboxOpen}
                onClose={() => setIsRefLightboxOpen(false)}
                activeKey={activeRefKey}
                onSelectKey={setActiveRefKey}
                resolvedImages={visualRefImages}
              />
            </div>
          </div>
        );
      })()}

      {/* =================================================================== */}
      {/* MODAL 2: INSPECT IQC CHECKPOINT DETAILS                             */}
      {/* =================================================================== */}
      {inspectIqcItem && (() => {
        const visualPassed = [
          inspectIqcItem.gooseneckCoverDirection,
          inspectIqcItem.batteryPcbSolderingPoint,
          inspectIqcItem.buttonPcbaSolderingPoint,
          inspectIqcItem.glueCheckPoint,
          inspectIqcItem.macIdSticker,
          inspectIqcItem.speakerWire,
        ].filter(Boolean).length;

        const functionalPassed = [
          inspectIqcItem.powerOnCheck,
          inspectIqcItem.speakerCheck,
          inspectIqcItem.ledCheck,
          inspectIqcItem.powerOffCheck,
        ].filter(Boolean).length;

        const totalPassed = visualPassed + functionalPassed;
        const finalStatus = (inspectIqcItem.finalStatus || "PASSED").toUpperCase();

        return (
          <div
            className="iqc-modal-overlay"
            onClick={() => setInspectIqcItem(null)}
          >
            <div
              className="iqc-modal-card"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="iqc-modal-header">
                <div className="iqc-modal-header-left">
                  <div className="iqc-modal-icon-badge">
                    <MdFactCheck size={22} />
                  </div>
                  <div className="iqc-modal-title-wrap">
                    <h3>IQC Inspection Checklist</h3>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "3px" }}>
                      <span className="iqc-modal-mac-chip">
                        {inspectIqcItem.macId}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(inspectIqcItem.macId);
                          showSnackbar(`Copied MAC ID ${inspectIqcItem.macId}`, "success");
                        }}
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          color: "#64748b",
                          padding: "2px",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                        title="Copy MAC ID"
                      >
                        <MdContentCopy size={14} />
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  className="iqc-modal-close-btn"
                  onClick={() => setInspectIqcItem(null)}
                  title="Close Modal"
                >
                  <MdClose size={20} />
                </button>
              </div>

              {/* Body */}
              <div className="iqc-modal-body">
                {/* Hero Summary Banner */}
                <div className="iqc-summary-banner">
                  <div className="iqc-summary-left">
                    <div className="iqc-summary-model">
                      <span>Model Name:</span>
                      <strong className="iqc-model-badge">
                        {inspectIqcItem.modelName || "Studds Link"}
                      </strong>
                    </div>
                    <div className="iqc-summary-date">
                      <MdHourglassEmpty size={14} />
                      <span>
                        Tested:{" "}
                        <strong>
                          {inspectIqcItem.createdAt
                            ? new Date(inspectIqcItem.createdAt).toLocaleString("en-GB")
                            : "—"}
                        </strong>
                      </span>
                    </div>
                  </div>

                  <div className="iqc-summary-right">
                    <div className="iqc-summary-score-chip">
                      <span>Checkpoints Passed:</span>
                      <strong>{totalPassed} / 10</strong>
                      <span style={{ color: totalPassed === 10 ? "#059669" : "#ea580c" }}>
                        ({totalPassed === 10 ? "100%" : `${totalPassed * 10}%`})
                      </span>
                    </div>

                    <div
                      className={`iqc-final-badge ${
                        inspectIqcItem.overall_status === "PASS" || finalStatus === "PASSED"
                          ? "pass"
                          : "fail"
                      }`}
                    >
                      {inspectIqcItem.overall_status === "PASS" || finalStatus === "PASSED" ? (
                        <>
                          <MdCheckCircle size={16} /> OVER ALL STATUS: PASS
                        </>
                      ) : (
                        <>
                          <MdCancel size={16} /> OVER ALL STATUS: FAIL
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* 2-Column Inspection Cards Grid */}
                <div className="iqc-cards-grid">
                  {/* Card 1: Visual Inspection (6 Points) */}
                  <div className="iqc-section-card">
                    <div className="iqc-card-header">
                      <div className="iqc-card-header-left">
                        <span className="iqc-card-title">1. Visual Inspection</span>
                        <span className="iqc-points-chip">6 Points</span>
                      </div>
                      <span
                        className={`iqc-badge-pill ${
                          inspectIqcItem.visualQcStatus === "PASS" ? "pass" : "fail"
                        }`}
                      >
                        {inspectIqcItem.visualQcStatus === "PASS" ? (
                          <>
                            <MdCheck size={13} /> PASS
                          </>
                        ) : (
                          <>
                            <MdClose size={13} /> FAIL
                          </>
                        )}
                      </span>
                    </div>

                    <div className="iqc-inspector-subbar">
                      <span className="iqc-inspector-name">
                        <MdPerson size={15} color="#64748b" />
                        <span>
                          Inspector:{" "}
                          <strong>
                            {inspectIqcItem.inspector_name ||
                              inspectIqcItem.qcInspector ||
                              inspectIqcItem.visualQcName ||
                              "Not Recorded"}
                          </strong>
                        </span>
                      </span>
                      <span className={`iqc-card-score ${visualPassed === 6 ? "all-passed" : ""}`}>
                        {visualPassed} / 6 Passed
                      </span>
                    </div>

                    <div className="iqc-items-list">
                      <div
                        className={`iqc-item-row ${
                          Boolean(inspectIqcItem.gooseneckCoverDirection) ? "is-pass" : "is-fail"
                        }`}
                      >
                        <div className="iqc-item-info">
                          <span
                            className={`iqc-item-dot ${
                              Boolean(inspectIqcItem.gooseneckCoverDirection) ? "pass" : "fail"
                            }`}
                          />
                          <span className="iqc-item-name">Gooseneck Cover Direction</span>
                        </div>
                        <span
                          className={`iqc-badge-pill ${
                            Boolean(inspectIqcItem.gooseneckCoverDirection) ? "pass" : "fail"
                          }`}
                        >
                          {Boolean(inspectIqcItem.gooseneckCoverDirection) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>

                      <div
                        className={`iqc-item-row ${
                          Boolean(inspectIqcItem.batteryPcbSolderingPoint) ? "is-pass" : "is-fail"
                        }`}
                      >
                        <div className="iqc-item-info">
                          <span
                            className={`iqc-item-dot ${
                              Boolean(inspectIqcItem.batteryPcbSolderingPoint) ? "pass" : "fail"
                            }`}
                          />
                          <span className="iqc-item-name">Battery & PCB Soldering</span>
                        </div>
                        <span
                          className={`iqc-badge-pill ${
                            Boolean(inspectIqcItem.batteryPcbSolderingPoint) ? "pass" : "fail"
                          }`}
                        >
                          {Boolean(inspectIqcItem.batteryPcbSolderingPoint) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>

                      <div
                        className={`iqc-item-row ${
                          Boolean(inspectIqcItem.buttonPcbaSolderingPoint) ? "is-pass" : "is-fail"
                        }`}
                      >
                        <div className="iqc-item-info">
                          <span
                            className={`iqc-item-dot ${
                              Boolean(inspectIqcItem.buttonPcbaSolderingPoint) ? "pass" : "fail"
                            }`}
                          />
                          <span className="iqc-item-name">Button PCBA Soldering Check</span>
                        </div>
                        <span
                          className={`iqc-badge-pill ${
                            Boolean(inspectIqcItem.buttonPcbaSolderingPoint) ? "pass" : "fail"
                          }`}
                        >
                          {Boolean(inspectIqcItem.buttonPcbaSolderingPoint) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>

                      <div
                        className={`iqc-item-row ${
                          Boolean(inspectIqcItem.glueCheckPoint) ? "is-pass" : "is-fail"
                        }`}
                      >
                        <div className="iqc-item-info">
                          <span
                            className={`iqc-item-dot ${
                              Boolean(inspectIqcItem.glueCheckPoint) ? "pass" : "fail"
                            }`}
                          />
                          <span className="iqc-item-name">Glue Check Point</span>
                        </div>
                        <span
                          className={`iqc-badge-pill ${
                            Boolean(inspectIqcItem.glueCheckPoint) ? "pass" : "fail"
                          }`}
                        >
                          {Boolean(inspectIqcItem.glueCheckPoint) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>

                      <div
                        className={`iqc-item-row ${
                          Boolean(inspectIqcItem.macIdSticker) ? "is-pass" : "is-fail"
                        }`}
                      >
                        <div className="iqc-item-info">
                          <span
                            className={`iqc-item-dot ${
                              Boolean(inspectIqcItem.macIdSticker) ? "pass" : "fail"
                            }`}
                          />
                          <span className="iqc-item-name">MAC ID Sticker</span>
                        </div>
                        <span
                          className={`iqc-badge-pill ${
                            Boolean(inspectIqcItem.macIdSticker) ? "pass" : "fail"
                          }`}
                        >
                          {Boolean(inspectIqcItem.macIdSticker) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>

                      <div
                        className={`iqc-item-row ${
                          Boolean(inspectIqcItem.speakerWire) ? "is-pass" : "is-fail"
                        }`}
                      >
                        <div className="iqc-item-info">
                          <span
                            className={`iqc-item-dot ${
                              Boolean(inspectIqcItem.speakerWire) ? "pass" : "fail"
                            }`}
                          />
                          <span className="iqc-item-name">Speaker Wire</span>
                        </div>
                        <span
                          className={`iqc-badge-pill ${
                            Boolean(inspectIqcItem.speakerWire) ? "pass" : "fail"
                          }`}
                        >
                          {Boolean(inspectIqcItem.speakerWire) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Functional Check (4 Points) */}
                  <div className="iqc-section-card">
                    <div className="iqc-card-header">
                      <div className="iqc-card-header-left">
                        <span className="iqc-card-title">2. Functional Check</span>
                        <span className="iqc-points-chip">4 Points</span>
                      </div>
                      <span
                        className={`iqc-badge-pill ${
                          inspectIqcItem.functionalQcStatus === "PASS" ? "pass" : "fail"
                        }`}
                      >
                        {inspectIqcItem.functionalQcStatus === "PASS" ? (
                          <>
                            <MdCheck size={13} /> PASS
                          </>
                        ) : (
                          <>
                            <MdClose size={13} /> FAIL
                          </>
                        )}
                      </span>
                    </div>

                    <div className="iqc-inspector-subbar">
                      <span className="iqc-inspector-name">
                        <MdPerson size={15} color="#64748b" />
                        <span>
                          Inspector:{" "}
                          <strong>
                            {inspectIqcItem.inspector_name ||
                              inspectIqcItem.qcInspector ||
                              inspectIqcItem.functionalQcName ||
                              "Not Recorded"}
                          </strong>
                        </span>
                      </span>
                      <span
                        className={`iqc-card-score ${functionalPassed === 4 ? "all-passed" : ""}`}
                      >
                        {functionalPassed} / 4 Passed
                      </span>
                    </div>

                    <div className="iqc-items-list">
                      <div
                        className={`iqc-item-row ${
                          Boolean(inspectIqcItem.powerOnCheck) ? "is-pass" : "is-fail"
                        }`}
                      >
                        <div className="iqc-item-info">
                          <span
                            className={`iqc-item-dot ${
                              Boolean(inspectIqcItem.powerOnCheck) ? "pass" : "fail"
                            }`}
                          />
                          <span className="iqc-item-name">Power ON Check</span>
                        </div>
                        <span
                          className={`iqc-badge-pill ${
                            Boolean(inspectIqcItem.powerOnCheck) ? "pass" : "fail"
                          }`}
                        >
                          {Boolean(inspectIqcItem.powerOnCheck) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>

                      <div
                        className={`iqc-item-row ${
                          Boolean(inspectIqcItem.speakerCheck) ? "is-pass" : "is-fail"
                        }`}
                      >
                        <div className="iqc-item-info">
                          <span
                            className={`iqc-item-dot ${
                              Boolean(inspectIqcItem.speakerCheck) ? "pass" : "fail"
                            }`}
                          />
                          <span className="iqc-item-name">Speaker Output Check</span>
                        </div>
                        <span
                          className={`iqc-badge-pill ${
                            Boolean(inspectIqcItem.speakerCheck) ? "pass" : "fail"
                          }`}
                        >
                          {Boolean(inspectIqcItem.speakerCheck) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>

                      <div
                        className={`iqc-item-row ${
                          Boolean(inspectIqcItem.ledCheck) ? "is-pass" : "is-fail"
                        }`}
                      >
                        <div className="iqc-item-info">
                          <span
                            className={`iqc-item-dot ${
                              Boolean(inspectIqcItem.ledCheck) ? "pass" : "fail"
                            }`}
                          />
                          <span className="iqc-item-name">LED Status Indicator</span>
                        </div>
                        <span
                          className={`iqc-badge-pill ${
                            Boolean(inspectIqcItem.ledCheck) ? "pass" : "fail"
                          }`}
                        >
                          {Boolean(inspectIqcItem.ledCheck) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>

                      <div
                        className={`iqc-item-row ${
                          Boolean(inspectIqcItem.powerOffCheck) ? "is-pass" : "is-fail"
                        }`}
                      >
                        <div className="iqc-item-info">
                          <span
                            className={`iqc-item-dot ${
                              Boolean(inspectIqcItem.powerOffCheck) ? "pass" : "fail"
                            }`}
                          />
                          <span className="iqc-item-name">Power OFF Check</span>
                        </div>
                        <span
                          className={`iqc-badge-pill ${
                            Boolean(inspectIqcItem.powerOffCheck) ? "pass" : "fail"
                          }`}
                        >
                          {Boolean(inspectIqcItem.powerOffCheck) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Inspection Notes & Remarks */}
                <div className="iqc-notes-card">
                  <div className="iqc-notes-header">
                    <MdInfoOutline size={16} />
                    <span>Inspection Notes & Remarks</span>
                  </div>
                  <p className="iqc-notes-text">
                    {inspectIqcItem.notes && inspectIqcItem.notes.trim()
                      ? inspectIqcItem.notes
                      : "Full inspection verified - all visual and functional tests passed."}
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="iqc-modal-footer">
                <button
                  type="button"
                  className="toolbar-btn secondary"
                  onClick={() => {
                    navigator.clipboard.writeText(inspectIqcItem.macId);
                    showSnackbar(`MAC ID ${inspectIqcItem.macId} copied to clipboard!`, "success");
                  }}
                  title="Copy MAC Address"
                >
                  <MdContentCopy size={15} />
                  <span>Copy MAC ID</span>
                </button>

                <button
                  type="button"
                  className="toolbar-btn primary"
                  onClick={() => setInspectIqcItem(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* =================================================================== */}
      {/* MODAL 3: DELETE IQC RECORD CONFIRMATION                             */}
      {/* =================================================================== */}
      {deleteIqcCandidate && (
        <div
          className="user-modal-overlay"
          onClick={() => !isDeletingIqc && setDeleteIqcCandidate(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.65)",
            backdropFilter: "blur(3px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999,
            padding: "20px",
          }}
        >
          <div
            className="user-modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff",
              borderRadius: "14px",
              width: "100%",
              maxWidth: "460px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: "#fee2e2",
                padding: "20px 24px",
                display: "flex",
                alignItems: "center",
                gap: "14px",
              }}
            >
              <MdDeleteOutline size={26} color="#dc2626" />
              <div>
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#991b1b" }}>
                  Delete IQC Quality Record
                </h3>
                <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#b91c1c" }}>
                  Remove test record for MAC: <strong>{deleteIqcCandidate.macId}</strong>
                </p>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                padding: "16px 24px",
                background: "#f9fafb",
              }}
            >
              <button
                type="button"
                className="toolbar-btn secondary"
                disabled={isDeletingIqc}
                onClick={() => setDeleteIqcCandidate(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="toolbar-btn"
                disabled={isDeletingIqc}
                onClick={confirmDeleteIqc}
                style={{ background: "#dc2626", color: "#fff", border: "none" }}
              >
                {isDeletingIqc ? "Deleting..." : "Delete IQC Record"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* MODAL 4: UPLOAD IQC INSPECTION (CSV / EXCEL)                        */}
      {/* =================================================================== */}
      {isIqcUploadModalOpen && (
        <div
          className="recon-modal-overlay"
          onClick={() => {
            if (!isIqcUploading) {
              setIsIqcUploadModalOpen(false);
              setIqcUploadFile(null);
              setIqcUploadPreview([]);
              if (iqcFileInputRef.current) iqcFileInputRef.current.value = "";
            }
          }}
        >
          <div
            className="recon-modal-card"
            style={{ maxWidth: "620px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="recon-modal-header"
              style={{ background: "#f0fdf4", borderBottomColor: "#bbf7d0" }}
            >
              <h3 style={{ color: "#166534" }}>
                <MdCloudUpload size={22} color="#15803d" />
                Upload IQC Inspection (CSV / Excel)
              </h3>
              <button
                type="button"
                onClick={() => {
                  if (!isIqcUploading) {
                    setIsIqcUploadModalOpen(false);
                    setIqcUploadFile(null);
                    setIqcUploadPreview([]);
                    if (iqcFileInputRef.current) iqcFileInputRef.current.value = "";
                  }
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#64748b",
                }}
              >
                <MdClose size={22} />
              </button>
            </div>

            <div className="recon-modal-body">
              {/* Sample Templates Section with the 2 Requested Buttons */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "14px 18px",
                  marginBottom: "20px",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <div>
                  <div style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b" }}>
                    Sample Templates
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>
                    Download template format with visual & functional checkpoints:
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="toolbar-btn secondary"
                    onClick={downloadIqcSampleCsv}
                    title="Download Sample CSV file"
                  >
                    <MdFileDownload size={16} />
                    <span>Download Sample CSV</span>
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn secondary"
                    onClick={downloadIqcSampleExcel}
                    title="Download Sample Excel (.xlsx) file"
                  >
                    <MdFileDownload size={16} />
                    <span>Download Sample Excel</span>
                  </button>
                </div>
              </div>

              {/* Hidden File Input */}
              <input
                type="file"
                ref={iqcFileInputRef}
                onChange={handleIqcFileSelect}
                accept=".xlsx, .xls, .csv"
                style={{ display: "none" }}
              />

              {/* Upload Dropzone */}
              <div
                className={`upload-dropzone ${iqcUploadFile ? "has-file" : ""}`}
                onClick={() => iqcFileInputRef.current?.click()}
                style={{
                  border: "2px dashed #cbd5e1",
                  borderRadius: "10px",
                  padding: "28px 20px",
                  textAlign: "center",
                  cursor: "pointer",
                  background: "#f8fafc",
                  marginBottom: "16px",
                  transition: "all 0.2s ease",
                }}
              >
                <MdCloudUpload size={40} color={iqcUploadFile ? "#15803d" : "#94a3b8"} />
                {iqcUploadFile ? (
                  <div style={{ marginTop: "10px" }}>
                    <div style={{ fontWeight: 700, color: "#166534" }}>{iqcUploadFile.name}</div>
                    <div style={{ fontSize: "12px", color: "#64748b" }}>
                      {(iqcUploadFile.size / 1024).toFixed(1)} KB — Click to choose different file
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: "10px" }}>
                    <div style={{ fontWeight: 600, color: "#334155" }}>
                      Click to choose or drag & drop CSV or Excel file here
                    </div>
                    <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "4px" }}>
                      Supports .xlsx, .xls, .csv
                    </div>
                  </div>
                )}
              </div>

              {/* Preview Table */}
              {iqcUploadPreview.length > 0 && (
                <div style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "#334155",
                      marginBottom: "8px",
                    }}
                  >
                    File Preview (First 5 Rows):
                  </div>
                  <div
                    style={{
                      maxHeight: "160px",
                      overflowY: "auto",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                    }}
                  >
                    <table
                      style={{
                        width: "100%",
                        fontSize: "11px",
                        borderCollapse: "collapse",
                      }}
                    >
                      <thead style={{ background: "#f1f5f9" }}>
                        <tr>
                          {Object.keys(iqcUploadPreview[0]).slice(0, 5).map((col) => (
                            <th
                              key={col}
                              style={{
                                padding: "6px 10px",
                                textAlign: "left",
                                borderBottom: "1px solid #e2e8f0",
                              }}
                            >
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {iqcUploadPreview.map((row, rIdx) => (
                          <tr key={rIdx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                            {Object.keys(iqcUploadPreview[0]).slice(0, 5).map((col) => (
                              <td
                                key={col}
                                style={{
                                  padding: "6px 10px",
                                  fontFamily: col.toLowerCase().includes("mac") ? "monospace" : "inherit",
                                }}
                              >
                                {String(row[col] ?? "")}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div
              className="recon-modal-footer"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                padding: "16px 20px",
                borderTop: "1px solid #e2e8f0",
              }}
            >
              <button
                type="button"
                className="toolbar-btn secondary"
                disabled={isIqcUploading}
                onClick={() => {
                  setIsIqcUploadModalOpen(false);
                  setIqcUploadFile(null);
                  setIqcUploadPreview([]);
                  if (iqcFileInputRef.current) iqcFileInputRef.current.value = "";
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="toolbar-btn primary"
                disabled={!iqcUploadFile || isIqcUploading}
                onClick={handleConfirmIqcUpload}
                style={{
                  background: "#15803d",
                  borderColor: "#15803d",
                  color: "#fff",
                  fontWeight: 700,
                }}
              >
                {isIqcUploading ? "Uploading & Processing..." : "Upload & Process Batch"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dedicated Top-Level Error Popup */}
      <ErrorPopup
        open={errorPopup.open}
        title={errorPopup.title}
        message={errorPopup.message}
        hint={errorPopup.hint}
        onClose={() => setErrorPopup((prev) => ({ ...prev, open: false }))}
        actionText="Go to Master Mac ID List"
        onAction={() => {
          setErrorPopup((prev) => ({ ...prev, open: false }));
          navigate("/dashboard/master-mac");
        }}
      />

      {/* Snackbar Alert */}
      <ShowSnacker
        open={snack.open}
        message={snack.message}
        type={snack.type}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
}

export default DashBoardIqcData;

